/**
 * Wholesale / Import-Export demo — full tour.
 *
 * Walks through: dashboard, shipments (container/BOL/landed cost), inventory
 * (origin/cost/margin/bulk upload), customers, invoicing (Zelle/ACH/card/wire),
 * tax-per-line, then settings. Uses fictional "Atlas Trade Imports" — no
 * real customer data is shown.
 */
export default {
  category: "wholesale",
  title: "Run your wholesale operation",
  initialRoute: "/atlas-trade/wholesale",

  steps: [
    {
      type: "narrate",
      text: "Hi there! Welcome to My Business Portal. Let me walk you through how it runs a wholesale, import, or export business.",
      duration: 11000,
    },
    {
      type: "point",
      target: '[data-testid="dashboard-content"]',
      text: "Your dashboard tracks every shipment by stage — at origin, on the water, at port, cleared customs, and received — all in one screen.",
      duration: 13000,
    },
    {
      type: "click",
      target: '[data-testid="nav-shipments"]',
      text: "Let's open the shipments view.",
      duration: 5500,
    },
    {
      type: "narrate",
      text: "Each shipment shows the container number, the bill of lading, the line items inside, your supplier cost, freight, and customs duty. Add those together and you get your full landed cost — the real number you use to price your products.",
      duration: 18000,
    },
    {
      type: "scroll",
      delta: 500,
      text: "Let's keep going.",
      duration: 3500,
    },
    {
      type: "click",
      target: '[data-testid="nav-inventory"]',
      text: "Inventory: every product with its country of origin, supplier cost, sell price, the margin between them, and current stock.",
      duration: 11500,
    },
    {
      type: "narrate",
      text: "Adding products in bulk is simple — upload a CSV, Excel sheet, Word document, or a PDF price list from your supplier and the system reads the columns and creates them for you.",
      duration: 13500,
    },
    {
      type: "click",
      target: '[data-testid="nav-customers"]',
      text: "Your customer book: every account with their orders, open balance, and full payment history. Bulk-import customers the same way you do products.",
      duration: 12500,
    },
    {
      type: "click",
      target: '[data-testid="nav-invoices"]',
      text: "And here's invoicing.",
      duration: 5000,
    },
    {
      type: "narrate",
      text: "Create a polished invoice in seconds. Accept Zelle, ACH, card, or wire transfers. Sales tax is auto-calculated per line item based on your tax setup — taxable lines and tax-exempt lines are separated for filing.",
      duration: 16000,
    },
    {
      type: "narrate",
      text: "When a payment lands, it's automatically tagged to the right customer. No manual reconciliation, no end-of-month spreadsheets.",
      duration: 11500,
    },
    {
      type: "navigate",
      url: "/atlas-trade/financials",
      text: "Financials has its own dedicated page — revenue trend, profit, margin — across any date range. And because this is the most sensitive screen in the whole app, you can lock it behind a four-digit privacy PIN with the toggle in the top right. Your team can still run shipments and invoices, but only you see the profit numbers.",
      duration: 22000,
    },
    {
      type: "navigate",
      url: "/atlas-trade/payments",
      text: "Payments has its own page too. Connect Stripe, Square, or PayPal with one click — buyers can pay any invoice online with card, Apple Pay, or Google Pay. Zelle's right there for the no-fee crowd, and when a payment lands the invoice flips to Paid on its own.",
      duration: 19500,
    },
    {
      type: "navigate",
      url: "/atlas-trade/settings",
      text: "And in settings: your business info, your logo — auto-generated by AI or upload your own with every previous version saved in history — line-item categories you can fully customize, like Container Fee or Customs Duty for an import operation — and your tax setup, all on one scrollable page.",
      duration: 21000,
    },
    {
      type: "narrate",
      text: "Free to start. No credit card required. Click Get Started up top and you're set up in minutes.",
      duration: 9000,
    },
  ],
};
